
import { HsbElement, HsbEntity, NodeModel, Units, Node } from './hsbcad-model';
import { IProjectDB } from './iproject-db';

export enum DataStoreType {
    EFS = 0,
    Database = 1
}
export class HsbProject {

    Units: Units;
    /**
     * The node model for this project
     */
    nodeModel: NodeModel;
    datastoreFolder: string = '';
    /**
     * The models belonging to this project.
     */
    HsbModels: { [key: string]: any };
    /**
     * Table containing all entities in this project
     * The key is the entity handle.
     */
    EntityTable: { [key: string]: { [key: string]: HsbEntity } };
    licenseID;
    baseUrl: string;
    docFiles: any[];
    /**
     * The URL to the data storage of this project
     */
    datastoreUrl: string;
    projectItem: IProjectDB | null = null;
    /**
     * The data ID for this project
     */
    projectID: string = '';
    /**
     * The amount of geometry buffers in this project
     */
    geomBuffersCount: number = 0;
    NodeStorage: DataStoreType;
    projectSource: any;
    docNodes!: { refnode: Node, modelinfonode: Node }[];

    constructor(baseUrl: string) {

        this.Units = new Units("mm");
        this.nodeModel = new NodeModel(null, null, this);
        this.HsbModels = {};
        this.EntityTable = {};
        this.licenseID = "notdefined";
        this.baseUrl = baseUrl;
        this.docFiles = [];
        this.datastoreUrl = '/api/v2/efs/datastore';
        this.NodeStorage = DataStoreType.EFS;
        this.projectSource = "hsbcad";
    };

    isMultiModel() {
        return Object.keys(this.HsbModels).length > 1;
    }

    /**
     * Returns all entities, optionally only the visible entities
     * @param visibleonly Should only visible entites be returned
     * @returns Array containing all (visible) entities.
     */
    allHsbEntities(visibleonly: boolean = false) {
        var entityTable = this.EntityTable;
        return Object.values(entityTable).flatMap((model) => Object.values(model)).filter((entity) => (entity && (!visibleonly || entity.visible)));
    }

    getProjectModelNode(modelId: string = '') {
        let hsbMakeModels = this.nodeModel.getNodesMetadataIdByItemType(this.nodeModel.ItemsRoot, 'hsbshare.projectmodel').map((node) => {
            return node.metadata.data;
        });
        let activeModel = null;
        if (modelId.length > 0) {
            activeModel = hsbMakeModels.filter(function (e) { return e.properties['modelId']?.value === modelId })[0];
        } else {
            activeModel = hsbMakeModels.filter(function (e) { return e.properties['active']?.value === true })[0];
        }
        if (!activeModel) return null;
        let activeModelNode = activeModel;
        return activeModelNode;
    }

    static normalizeSelectionforElements = function (entities: HsbEntity[]) {

        if (!entities) return null;
        if (entities.length === 1) return entities;
        let elements = entities.filter(function (e) { return (e instanceof HsbElement) });
        if (elements.length === 0) return entities;

        let returnSelection = entities.filter(function (entity) { return (entity instanceof HsbElement) || !elements.filter(function (e) { return e === entity.getElement() })[0] })

        return returnSelection;
    }

    static isOneElement = function (entities: HsbEntity[]) {

        if (!entities) return false;

        if (entities.length === 1) return true;

        let elements = entities.filter(function (e) { return e instanceof HsbElement });

        if (elements.length > 1) return false;

        if (elements[0] instanceof HsbElement) {
            let element = elements[0];
            if (!element.entities) return true;

            entities.forEach(entity => {
                if (entity !== element) {
                    if (!element.entities.filter(function (e) { return e === entity })[0]) {
                        return false;
                    }
                }
            });

            return element;
        }
    }

    findEntitiesByObjectType(visibleOnly: boolean, objectType: string) {

        var foundenties: HsbEntity[] = [];
        var entityTable = this.EntityTable;

        Object.keys(entityTable).forEach(function (drawingname) {

            var model = entityTable[drawingname];

            Object.keys(model).forEach(function (entitykey) {

                var entity = model[entitykey];

                if (!visibleOnly || entity.visible) {
                    if (objectType && (objectType === 'any' || entity.objecttype === objectType)) {
                        foundenties.push(entity);
                    }
                }
            });
        });

        return foundenties;
    }

    /**
     * Filter the given entities based on the type provided
     * @param entities The array to be filtered
     * @param instanceType The type of entities to return
     * @returns An array containing all entities that are of the given type
     */
    filterEntitiesOnEntityType<T extends HsbEntity = HsbEntity>(entities: HsbEntity[], instanceType: (...args: any[]) => T) {

        return entities.filter((entity: HsbEntity) => {
            return (entity instanceof instanceType)
        });
    }

    filterEntitiesOnObjectType(entities: HsbEntity[], objectType: string) {
        return entities.filter(entity => {
            return (objectType === 'any' || entity.objecttype === objectType)
        });
    }

    findEntities<T extends HsbEntity = HsbEntity>(instanceType: (...args: any[]) => T) {

        var foundenties: HsbEntity[] = [];
        var entityTable = this.EntityTable;

        Object.keys(entityTable).forEach(function (drawingname) {

            var model = entityTable[drawingname];

            Object.keys(model).forEach(function (entitykey) {

                var entity = model[entitykey];
                if (entity instanceof instanceType) {
                    foundenties.push(entity);
                }
            });
        });

        return foundenties;
    }
    /**
     * Searches for an entity for the given model and entity handle
     * @param modelId The ID of the model to search in
     * @param entityKey The handle of the emtity to look for
     * @returns The entity for the given params, undefined otherwise
     */
    public getEntity(modelId: string, entityKey: string) {
        if (!modelId || !entityKey)
            throw new Error('Provided modelID or entity handle can not be undefined or null!');

        var model = this.EntityTable[modelId];
        if (!model)
            return null;

        return model[entityKey];
    }

    getSelectTreeNodeData(selectednodes) {

        var selectedentities: HsbEntity[] = [];
        var selectHash = {};

        var findselectnodes = function (selectnodes) {

            selectnodes.forEach(node => {
                var foundentities;

                if (node.hsbmodel && !node.hsbmodel.ProjectSource && node.hsbmodel.Entities.length > 0 && (node.groupsite != null || node.set != null)) {
                    foundentities = node.hsbmodel.allHsbEntities();
                }
                else if (node.entity && (node.entity instanceof HsbElement || node.entity instanceof HsbEntity)) {
                    foundentities = node.entity;
                }
                if (node.children) {

                    foundentities = node.children;
                    if (node.entity) {
                        if (!selectHash[node.entity.hsbmodel.modelId + node.entity.handle]) {

                            selectHash[node.entity.hsbmodel.modelId + node.entity.handle] = {};
                            selectedentities.push(node.entity);
                        }
                    }

                    if (foundentities.length > 0) {
                        return findselectnodes(foundentities);
                    }
                }

                if (foundentities != null) {

                    if (!selectHash[foundentities.hsbmodel.modelId + foundentities.handle]) {
                        selectHash[foundentities.hsbmodel.modelId + foundentities.handle] = {};
                        selectedentities.push(foundentities);
                    }
                }
            });
        }

        findselectnodes(selectednodes);

        return selectedentities;
    }
    getParentEntity(entity) {
        if (!entity || !entity.node || !entity.node.parent) return null;
        let node = entity.node.parent;
        let parentEntity = null;

        do {
            parentEntity = node.entity;
            if (parentEntity) {
                node = null;
            } else {
                node = node.parent;
            }
        }
        while (node)

        return parentEntity;
    }

    includeChildEntities(entities) {
        // This will ensure we add all child entities to the input entities.
        let nodes = entities.map(function (entity) { return entity.node });
        return this.getSelectTreeNodeData(nodes);
    }

    getAllPropertyFields(entities, objectType) {

        let filteredEntities = this.filterEntitiesOnObjectType(entities, objectType);

        // Build a hash of unique fieldname name.
        let propertyHash = {};
        filteredEntities.forEach(entity => {

            Object.keys(entity.props).forEach(function (key) {

                if (key.split('.').length !== 2) {
                    if (!propertyHash[key]) {
                        propertyHash[key] = 1;
                    }
                }
            });
        });

        let options:{value: string,viewValue:string}[] = [];

        Object.keys(propertyHash).forEach(function (key) {

            options.push({ value: key, viewValue: key })
        });

        return options;
    }

    searchAllEntities(entities, objectType, key, value) {

        let filteredEntities = this.filterEntitiesOnObjectType(entities, objectType);
        let foundenties: HsbEntity[] = [];
        let values: string[] = [];

        if (Array.isArray(value)) {
            values = value;
        }
        else {
            values.push(value);
        }

        let valueHash = {};

        values.forEach(val => {
            valueHash[val] = 1;
        });

        filteredEntities.forEach(entity => {
            if ((!key || entity.props[key]) && (!value || valueHash[entity.props[key].val])) {
                foundenties.push(entity);
            }
        });

        return foundenties;
    }
}



